/**
 * A file of id links: `their id, our code`, one per line.
 *
 * Read here only to send the rows to omnium, which says what each would change
 * before anything is saved. Commas, semicolons or tabs; quotes for a value that
 * holds one; a first line of column names is skipped.
 */

export type CsvLink = { line: number; externalId: string; code: string };

export type CsvRead = {
  rows: CsvLink[];
  /** Lines that could not be read, in words. */
  problems: string[];
  hadHeader: boolean;
};

function delimiterOf(line: string): string {
  const counts = [",", ";", "\t"].map((mark) => ({ mark, count: line.split(mark).length - 1 }));
  counts.sort((a, b) => b.count - a.count);
  return counts[0].count > 0 ? counts[0].mark : ",";
}

/** One line into cells, honouring double quotes. */
export function splitLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (quoted) {
      if (char === '"' && line[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (char === '"') {
        quoted = false;
      } else {
        cell += char;
      }
    } else if (char === '"' && cell.trim() === "") {
      quoted = true;
      cell = "";
    } else if (char === delimiter) {
      cells.push(cell.trim());
      cell = "";
    } else {
      cell += char;
    }
  }
  cells.push(cell.trim());
  return cells;
}

function looksLikeHeader(cells: string[]): boolean {
  const text = cells.join(" ").toLowerCase();
  return /\btheir\b|\bexternal\b|\bour\b|\bcode\b/.test(text) && !/\d/.test(text);
}

export function readLinksCsv(text: string): CsvRead {
  const lines = text.replace(/^\uFEFF/, "").split(/\r\n|\n|\r/);
  const firstFilled = lines.find((line) => line.trim() !== "") ?? "";
  const delimiter = delimiterOf(firstFilled);
  const rows: CsvLink[] = [];
  const problems: string[] = [];
  let hadHeader = false;
  let seenFirst = false;
  lines.forEach((raw, index) => {
    if (raw.trim() === "") return;
    const cells = splitLine(raw, delimiter);
    if (!seenFirst) {
      seenFirst = true;
      if (looksLikeHeader(cells)) {
        hadHeader = true;
        return;
      }
    }
    const line = index + 1;
    if (cells.length < 2) {
      problems.push(`Line ${line} has one value. Each line needs their id and our code.`);
      return;
    }
    if (cells.length > 2 && cells.slice(2).some((cell) => cell !== "")) {
      problems.push(`Line ${line} has more than two values. Only the first two are used.`);
    }
    rows.push({ line, externalId: cells[0], code: cells[1] });
  });
  return { rows, problems, hadHeader };
}

function cell(value: string): string {
  return /[",\n;]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function linksCsv(rows: { externalId: string; code: string | null | undefined }[]): string {
  return ["their id,our code", ...rows.map((row) => `${cell(row.externalId)},${cell(row.code ?? "")}`)].join("\n");
}

export type UploadAction = "add" | "move" | "same" | "not_found" | "ambiguous" | "duplicate" | "blank";

const ACTION_WORDS: Record<UploadAction, string> = {
  add: "New link",
  move: "Moves to another record",
  same: "Already linked",
  not_found: "No record has this code",
  ambiguous: "More than one record has this code",
  duplicate: "Listed twice in the file",
  blank: "An empty value",
};

export function actionWords(action: string): string {
  return ACTION_WORDS[action as UploadAction] ?? action;
}

/** What saving would do, in one sentence. */
export function uploadSummary(counts: Partial<Record<UploadAction, number>>, replace: boolean): string {
  const add = counts.add ?? 0;
  const move = counts.move ?? 0;
  const same = counts.same ?? 0;
  const bad = (counts.not_found ?? 0) + (counts.ambiguous ?? 0) + (counts.duplicate ?? 0) + (counts.blank ?? 0);
  const parts: string[] = [];
  parts.push(`${add.toLocaleString()} new ${add === 1 ? "link" : "links"}`);
  if (move) parts.push(replace ? `${move} moved to another record` : `${move} that point elsewhere left alone`);
  if (same) parts.push(`${same} already linked`);
  if (bad) parts.push(`${bad} that cannot be used`);
  return `Saving makes ${parts.join(", ")}.`;
}
